import React, { lazy, useRef, useState } from 'react'
import { css } from '@emotion/core'
import { boolAttr } from '@fs/zion-frontend-friends'
import { Separator } from '@fs/zion-ui'
import { useUser } from '@fs/zion-user'
import { SocialComment, NoticeLoading } from '@fs/zion-icon'
import { useTranslation } from 'react-i18next'
import { i18n } from '@fs/zion-locale'
import { Link } from '@fs/zion-router'
import useIsMobile from '../../hooks/useIsMobile'
import LikeDislike from '../LikeDislike'
import RichTextDisplayer from '../RichTextDisplayer'
import LargeLoadingSpinner from '../LargeLoadingSpinner'
import SeriesBadge from '../series/SeriesBadge'
import DetailsPageContainer from '../DetailsPageContainer'
import useJoinSessionChatRoom from '../../hooks/useJoinSessionChatRoom'
import PillButton from '../PillButton'
import AddToPlaylistButton from '../AddToPlaylistButton'
import { RTCInitialState } from '../../state/RTCReducer'
import CountdownPlaceholder from '../home/CountDownPlaceholder'
import VideoSwitcher from '../VideoSwitcher'
import RootsTechOverlay from '../RootsTechOverlay'
import { mainStageAccountId } from '../../global/bitMovinAccountId'
import { sanitizeChatRoomTitle } from '../../global/sanitize-chat-room'
import useSearchForChatRoom from '../../hooks/useSearchForChatRoom'
import { useChatOverlayContext } from '../chat/ChatOverlayProvider'
import { rootstechExpoPreshowMS } from '../../helpers/eventDates'
import getRelativeUrl from '../../helpers/UrlHelper'
import { Chat } from '../../global/chatSettingsConfig'

const ResponsiveYoutubeVideo = lazy(() => import('../ResponsiveYoutubeVideo'))

const likeDislikeCss = css`
  display: flex;
  justify-content: flex-end;
  margin-top: 16px;
`

const sessionDetailsCss = css`
  display: grid;
  grid-template-columns: minmax(60px, 160px) 1fr;
  grid-column-gap: 40px;
  &[is-mobile] {
    grid-template-columns: 1fr;
  }
`

const descriptionCss = css`
  word-wrap: break-word;
  max-width: 740px;
  font-size: 18px;
  line-height: 28px;
`

const profileImageCss = css`
  object-fit: cover;
  border-radius: 160px;
  width: 20vh;
  height: 20vh;
  min-width: 60px;
  max-width: 160px;
  min-height: 60px;
  max-height: 160px;
  background: #fff9;
  outline: inset transparent 1px;

  *[is-mobile] & {
    margin-bottom: 20px;
  }
`

const headingCss = css`
  font-size: 42px;
  line-height: 52px;
  font-weight: 700;

  *[is-mobile] & {
    margin-top: 12px;
    font-size: 24px;
    line-height: 28px;
  }
`

const subHeadingCss = css`
  font-size: 24px;
  line-height: 32px;

  *[is-mobile] & {
    font-size: 18px;
    line-height: 24px;
  }
`
const languageBadgeStyle = css`
  background: var(--background-secondary);
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  width: fit-content;
  padding: 4px 12px;
  border-radius: 3.6px;
  text-transform: uppercase;
  height: 24px;
  font-weight: 700;
  font-size: 14px;
  & + * {
    margin-left: 4px;
  }
`

const creatorNameCss = css`
  padding: 0px 15px 0px 0px;
  display: inline-block;
`

const LanguageBadge = ({ language }) => {
  return (
    <div css={languageBadgeStyle}>
      <div>{language}</div>
    </div>
  )
}

const showVideo = ({ isSeries, sessionStartTime }) => {
  let canPlay = true
  if (!isSeries && sessionStartTime) {
    const nowMS = Date.now() + RTCInitialState.timeOffset
    if (nowMS < sessionStartTime) {
      canPlay = false
    }
  }
  return canPlay
}

export default function SessionView({
  nav,
  children,
  heading,
  subheading,
  description,
  profilePhoto,
  loading,
  data,
  isSeries,
  isInPlaylist,
  spokenLanguage,
  videoProviderList,
  videoProvider,
  isMainStageLiveVariant,
  creators,
}) {
  const isMobile = useIsMobile()
  const user = useUser()
  const [t] = useTranslation()
  const [joinSessionChatRoom, joinState] = useJoinSessionChatRoom()
  const startTime = data?.date?.timestamp ?? rootstechExpoPreshowMS

  let videoDetails = videoProviderList?.[0]?.videoProvider ?? videoProvider
  const [isOpen, setIsOpen] = useState(false)
  const buttonRef = useRef()

  const [searchForChatRoom] = useSearchForChatRoom()
  const { isOpen: isChatOpen, setIsOpen: setIsChatOpen } = useChatOverlayContext()

  if (isMainStageLiveVariant && videoDetails?.accountId) {
    // We have to use a different account for the main stage live stream
    videoDetails = { ...videoDetails, accountId: mainStageAccountId }
  }

  const handleClick = async () => {
    const validTitle = sanitizeChatRoomTitle(heading)
    const existingChatRoom = await searchForChatRoom({
      searchStr: validTitle,
      type: 'SESSION',
      matchExactly: true,
    })

    if (existingChatRoom?.parent) {
      if (!isChatOpen) {
        setIsChatOpen(true)
      }
      window.chatHistory.push(`/children-chat-room/${existingChatRoom.jid}/${encodeURIComponent(validTitle)}`)
    } else {
      joinSessionChatRoom(validTitle)
    }
  }

  if (loading) {
    return <LargeLoadingSpinner />
  }

  return (
    <DetailsPageContainer>
      {nav}
      {!isMobile && <Separator size="xxs" />}
      {videoDetails ? (
        <>
          {!isMobile && <Separator size="xl" />}

          {showVideo({ isSeries, sessionStartTime: startTime }) ? (
            <VideoSwitcher videoProvider={videoDetails} autoSelectedLanguage={i18n.language} title={heading} />
          ) : (
            <CountdownPlaceholder startTime={startTime} />
          )}

          {user.signedIn && data?.contentId ? (
            <div css={likeDislikeCss}>
              <LikeDislike contentId={data?.contentId} />
            </div>
          ) : (
            <Separator size="lg" />
          )}

          <Separator />
        </>
      ) : (
        <Separator size="xl" />
      )}

      <div css={sessionDetailsCss} is-mobile={boolAttr(isMobile)}>
        {!(isMobile && isSeries) && profilePhoto ? (
          <img src={profilePhoto} alt="" aria-hidden css={profileImageCss} />
        ) : (
          <div />
        )}

        <div>
          {isSeries && (
            <>
              <SeriesBadge />
              <Separator size="xxs" />
            </>
          )}
          {spokenLanguage && videoDetails?.providerType !== 'brightcove' && (
            <>
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <LanguageBadge language={spokenLanguage} />
                <PillButton
                  noBorder
                  data-testid="cc-instructions"
                  onClick={() => {
                    setIsOpen(true)
                  }}
                  emphasis="medium"
                >
                  {t('session.watch-how-to-autotranslate.label', 'Watch how to auto-translate')}
                </PillButton>
              </div>

              <Separator size="xxs" />
            </>
          )}
          <h1 css={headingCss}>{heading || data?.title}</h1>
          {creators && (
            <>
              <Separator size="xxs" />
              {creators?.map((creator) => {
                return (
                  <Link emphasis="medium" css={creatorNameCss} to={getRelativeUrl(creator?.url)}>
                    <h2 css={subHeadingCss} is-mobile>
                      {creator?.name}
                    </h2>
                  </Link>
                )
              })}
            </>
          )}

          <Separator size={subheading ? 'md' : 'xxs'} />
          <div css={descriptionCss}>
            <RichTextDisplayer richText={description || data?.description} />
            {data?.recipe && <RichTextDisplayer richText={data.recipe} />}
            {user.signedIn && (
              <>
                <Separator size={isMobile ? 'xs' : 'md'} />
                <AddToPlaylistButton isInPlaylist={isInPlaylist} contentUUID={data?.contentId} isSeries={isSeries} />
                {!(isSeries || isMainStageLiveVariant) && Chat.Settings.SITE_CHAT_ENABLED && (
                  <>
                    <Separator />
                    <PillButton
                      data-testid="join-session-chat-room"
                      emphasis="medium"
                      onClick={handleClick}
                      Icon={joinState.pending ? NoticeLoading : SocialComment}
                      disabled={joinState.pending}
                    >
                      {t('chat.join-chat-room', 'Join Chat Room')}
                    </PillButton>
                  </>
                )}
              </>
            )}
          </div>
        </div>
      </div>
      <RootsTechOverlay isOpen={isOpen} setIsOpen={setIsOpen} focusBackRef={buttonRef}>
        <ResponsiveYoutubeVideo noMinHeight youTubeId="DT0OM9Eo0JI" />
      </RootsTechOverlay>
      {children && <Separator size={isMobile ? 'lg' : 'xxl'} />}
      {children}
    </DetailsPageContainer>
  )
}
